import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './i18n/LanguageContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login/Login';
import Activate from './pages/Auth/Activate';
import StaffLanding from './pages/StaffLanding/StaffLanding';
import Dashboard from './pages/Dashboard/Dashboard';
import Profile from './pages/Profile/Profile';
import StaffProfile from './pages/Profile/StaffProfile';
import Members from './pages/Members/Members'; // Admin/Staff member management
import Member from './pages/Member/Member'; // Individual member profile
import Contributions from './pages/Contributions/Contributions';
import Loans from './pages/Loans/Loans';
import Communications from './pages/Communications/Communications';
import Meetings from './pages/Meetings/Meetings';
import Elections from './pages/Elections/Elections';
import Trainings from './pages/Trainings/Trainings';
import Engagement from './pages/Engagement/Engagement';
import Cases from './pages/Cases/Cases';
import CBA from './pages/CBA/CBA';
import MyId from './pages/MyId/MyId';
import IDCards from './pages/IDCards/IDCards';
import CardTemplates from './pages/CardTemplates/CardTemplates';
import CardTemplateDesigner from './pages/CardTemplates/CardTemplateDesigner';
import Library from './pages/Library/Library';
import Support from './pages/Support/Support';
import AiAssistant from './pages/AiAssistant/AiAssistant';
import Calendar from './pages/Calendar/Calendar';
import Reports from './pages/Reports/Reports';
import Users from './pages/Users/Users';
import Branches from './pages/Branches/Branches';
import Recruitment from './pages/Recruitment/Recruitment';
import Accounting from './pages/Accounting/Accounting';
import StaffRequisitions from './pages/StaffRequisitions/StaffRequisitions';
import Settings from './pages/Settings/Settings';
import SmsLogs from './pages/SmsLogs/SmsLogs';
import SystemLogs from './pages/SystemLogs/SystemLogs';
import VerifyId from './pages/MyId/VerifyId';
import Careers from './pages/Careers/Careers';
import ComingSoon from './pages/Careers/ComingSoon';
import api from './api';
import './App.css';

const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, user, hasPermission, loading } = useAuth();
    const [portalSettings, setPortalSettings] = useState(null);
    useEffect(() => { api.getBrandingSettings().then(response => setPortalSettings(response?.data || {})).catch(() => setPortalSettings({})); }, []);

    if (loading || portalSettings === null) {
        return (
            <div className="loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (!isAuthenticated) return <Navigate to={portalSettings.maintenance_mode === '1' ? "/coming-soon" : "/login"} replace />;
    if (portalSettings.maintenance_mode === '1' && user?.role !== 'admin' && !hasPermission('recruitment.manage')) {
        return <Navigate to="/coming-soon" replace />;
    }
    return children;
};

const PortalOrCareers = ({ children }) => window.location.hostname.toLowerCase().startsWith('careers.') ? <Careers /> : children;

const PermissionRoute = ({ permission, children }) => {
    const { user, hasPermission, loading } = useAuth();
    if (loading) return <div className="loading"><div className="loading-spinner"></div></div>;
    if (user?.role === 'member' && !user?.profile_completed) return <Navigate to="/profile" replace />;
    return hasPermission(permission) ? children : <Navigate to="/dashboard" replace />;
};

const PublicRoute = ({ children }) => {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) {
        return (
            <div className="loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return children;
    }

    // If authenticated, redirect to appropriate home page based on role
    if (user && ['admin', 'treasurer', 'secretary', 'chair', 'union_official', 'branch_leader', 'committee', 'auditor'].includes(user.role)) {
        return <Navigate to="/staff-landing" />;
    }
    
    return <Navigate to="/dashboard" />;
};

const DefaultRoute = () => {
    const { user, loading } = useAuth();
    
    // Show loading while auth is loading
    if (loading) {
        return (
            <div className="loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }
    
    // Staff roles go to staff landing page (modules view)
    if (user && ['admin', 'treasurer', 'secretary', 'chair', 'union_official', 'branch_leader', 'committee', 'auditor'].includes(user.role)) {
        return <Navigate to="/staff-landing" />;
    }
    
    if (user?.role === 'member' && !user?.profile_completed) {
        return <Navigate to="/profile" />;
    }

    // Members with a complete profile go to dashboard
    return <Navigate to="/dashboard" />;
};

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={
                <PublicRoute>
                    <Login />
                </PublicRoute>
            } />
            <Route path="/activate" element={<Activate />} />
            <Route path="/verify-id/:token" element={<VerifyId />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/careers/jobs/:id" element={<Careers />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route path="/" element={
                <PortalOrCareers><ProtectedRoute><Layout /></ProtectedRoute></PortalOrCareers>
            }>
                <Route index element={<DefaultRoute />} />
                <Route path="staff-landing" element={<StaffLanding />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="profile" element={<Profile />} />
                <Route path="staff-profile" element={<PermissionRoute permission="staff_profile.read"><StaffProfile /></PermissionRoute>} />
                <Route path="member-profile" element={<Member />} />
                <Route path="members" element={<PermissionRoute permission="members.read"><Members /></PermissionRoute>} />
                <Route path="members/new" element={<PermissionRoute permission="members.create"><Members /></PermissionRoute>} />
                <Route path="members/:id" element={<PermissionRoute permission="members.read"><Members /></PermissionRoute>} />
                <Route path="contributions" element={<PermissionRoute permission="contributions.read"><Contributions /></PermissionRoute>} />
                <Route path="accounting/*" element={<PermissionRoute permission="finance.read"><Accounting /></PermissionRoute>} />
                <Route path="staff-requisitions" element={<PermissionRoute permission="requisitions.read"><StaffRequisitions /></PermissionRoute>} />
                <Route path="loans" element={<Loans />} />
                <Route path="communications" element={<PermissionRoute permission="communications.read"><Communications /></PermissionRoute>} />
                <Route path="meetings" element={<PermissionRoute permission="meetings.read"><Meetings /></PermissionRoute>} />
                <Route path="meetings/:id" element={<Meetings />} />
                <Route path="elections" element={<PermissionRoute permission="elections.read"><Elections /></PermissionRoute>} />
                <Route path="elections/:id" element={<Elections />} />
                <Route path="trainings" element={<Trainings />} />
                <Route path="engagement" element={<PermissionRoute permission="engagement.read"><Engagement /></PermissionRoute>} />
                <Route path="cases" element={<PermissionRoute permission="cases.read"><Cases /></PermissionRoute>} />
                <Route path="cba" element={<PermissionRoute permission="cba.read"><CBA /></PermissionRoute>} />
                <Route path="my-id" element={<PermissionRoute permission="id_cards.read"><MyId /></PermissionRoute>} />
                <Route path="id-cards" element={<PermissionRoute permission="id_cards.read"><IDCards /></PermissionRoute>} />
                <Route path="card-templates" element={<PermissionRoute permission="id_cards.manage"><CardTemplates /></PermissionRoute>} />
                <Route path="card-templates/create" element={<PermissionRoute permission="id_cards.manage"><CardTemplateDesigner /></PermissionRoute>} />
                <Route path="card-templates/edit/:id" element={<PermissionRoute permission="id_cards.manage"><CardTemplateDesigner /></PermissionRoute>} />
                <Route path="library" element={<Library />} />
                <Route path="support" element={<PermissionRoute permission="support.read"><Support /></PermissionRoute>} />
                <Route path="assistant" element={<PermissionRoute permission="ai.use"><AiAssistant /></PermissionRoute>} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="branches" element={<Branches />} />
                <Route path="recruitment" element={<Recruitment />} />
                <Route path="reports" element={<PermissionRoute permission="reports.read"><Reports /></PermissionRoute>} />
                <Route path="users" element={<PermissionRoute permission="admin.users"><Users /></PermissionRoute>} />
                <Route path="settings" element={<PermissionRoute permission="admin.users"><Settings /></PermissionRoute>} />
                <Route path="sms-logs" element={<PermissionRoute permission="admin.users"><SmsLogs /></PermissionRoute>} />
                <Route path="system-logs" element={<PermissionRoute permission="admin.users"><SystemLogs /></PermissionRoute>} />
            </Route>
            <Route path="*" element={<DefaultRoute />} />
        </Routes>
    );
}

function App() {
    return (
        <Router>
            <LanguageProvider>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </LanguageProvider>
        </Router>
    );
}

export default App;
